import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Login as LoginIcon, Email as EmailIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import AppStyles from '../styles/AppStyles';

function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState('email'); // 'email' or 'code'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [codeError, setCodeError] = useState(false);
  
  const { sendVetLoginCode, verifyVetLoginCode, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Check for account deleted success message from sessionStorage
  useEffect(() => {
    const accountDeletedFlag = sessionStorage.getItem('accountDeleted');
    
    if (accountDeletedFlag === 'true') {
      setSuccess(t('login.accountDeletedSuccess'));
      // Clear the flag from sessionStorage so it doesn't show again
      sessionStorage.removeItem('accountDeleted');
    }
  }, [t]);

  // Redirect to dashboard if user is already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailError(false);
    setError('');
  };

  const handleCodeChange = (e) => {
    // Only allow numbers and limit to 6 digits
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
    setCodeError(false);
    setError('');
  };

  const validateEmail = () => {
    const isValid = email && email.includes('@');
    setEmailError(!isValid);
    return isValid;
  };

  const handleSendCode = async (e) => {
    e?.preventDefault();
    
    if (!validateEmail()) {
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await sendVetLoginCode(email);
      if (result.success) {
        setSuccess(t('login.verificationCodeSent', { email }));
        setStep('code');
      } else {
        setError(result.error || t('login.failedToSendCode'));
      }
    } catch (err) {
      setError(t('login.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e?.preventDefault();
    
    if (!verificationCode || verificationCode.length !== 6) {
      setCodeError(true);
      setError(t('login.invalidCode'));
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await verifyVetLoginCode(email, verificationCode);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || t('login.invalidVerificationCode'));
        setCodeError(true);
      }
    } catch (err) {
      setError(t('login.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setVerificationCode('');
    setError('');
    setSuccess('');
    setCodeError(false);
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${AppStyles.colors.background.default} 0%, ${AppStyles.colors.background.secondary} 100%)`,
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Don't render login form if already authenticated (will redirect)
  if (isAuthenticated) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${AppStyles.colors.background.default} 0%, ${AppStyles.colors.background.secondary} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Paper
          elevation={8}
          sx={{
            padding: { xs: 3, sm: 5 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            borderRadius: 3,
            background: `linear-gradient(145deg, ${AppStyles.colors.background.paper} 0%, ${AppStyles.colors.background.secondary} 100%)`,
            border: `1px solid ${AppStyles.colors.border.light}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 3,
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                mb: 3,
                display: 'flex',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '60px',
                    height: '2px',
                    background: `linear-gradient(90deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
                    borderRadius: 1,
                  }
                }}
              >
                <img
                  src="/images/logo/textAndIconColor.png"
                  alt="Pawwi Logo"
                  style={{
                    height: '40px',
                    width: 'auto',
                    maxWidth: '320px',
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))',
                  }}
                />
              </Box>
            </Box>
            
            <Typography 
              component="h1" 
              variant="h3" 
              gutterBottom
              sx={{ 
                color: AppStyles.colors.primary.dark,
                fontWeight: AppStyles.typography.fontWeight.bold,
                textAlign: 'center',
                mb: 1,
              }}
            >
              {t('login.vetPortal')}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box 
            component="form" 
            onSubmit={step === 'email' ? handleSendCode : handleVerifyCode}
            noValidate
            sx={{ 
              width: '100%',
              mt: 4,
            }}
          >
            {step === 'email' ? (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label={t('login.emailAddress')}
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={handleEmailChange}
                  type="email"
                  error={emailError}
                  helperText={emailError ? t('login.emailRequired') : ''}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: emailError ? AppStyles.colors.error.main : AppStyles.colors.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: emailError ? AppStyles.colors.error.main : AppStyles.colors.primary.main,
                      },
                      '&.Mui-error fieldset': {
                        borderColor: AppStyles.colors.error.main,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: emailError ? AppStyles.colors.error.main : AppStyles.colors.primary.main,
                    },
                    '& .MuiInputLabel-root.Mui-error': {
                      color: AppStyles.colors.error.main,
                    },
                    '& .MuiFormHelperText-root.Mui-error': {
                      color: AppStyles.colors.error.main,
                    },
                    mb: 3,
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ 
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: AppStyles.typography.fontSize.lg,
                    fontWeight: AppStyles.typography.fontWeight.medium,
                    textTransform: 'none',
                    boxShadow: AppStyles.shadows.lg,
                    background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.primary.light} 100%)`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${AppStyles.colors.primary.dark} 0%, ${AppStyles.colors.primary.main} 100%)`,
                      boxShadow: AppStyles.shadows.xl,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <EmailIcon />}
                >
                  {loading ? t('login.sending') : t('login.sendVerificationCode')}
                </Button>
              </>
            ) : (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="code"
                  label={t('login.verificationCode')}
                  name="code"
                  autoFocus
                  value={verificationCode}
                  onChange={handleCodeChange}
                  type="text"
                  inputProps={{
                    maxLength: 6,
                    style: {
                      textAlign: 'center',
                      fontSize: '24px',
                      letterSpacing: '8px',
                      fontFamily: 'monospace',
                    }
                  }}
                  error={codeError}
                  helperText={codeError ? t('login.invalidCode') : t('login.enterCodeFromEmail')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: codeError ? AppStyles.colors.error.main : AppStyles.colors.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: codeError ? AppStyles.colors.error.main : AppStyles.colors.primary.main,
                      },
                      '&.Mui-error fieldset': {
                        borderColor: AppStyles.colors.error.main,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: codeError ? AppStyles.colors.error.main : AppStyles.colors.primary.main,
                    },
                    '& .MuiInputLabel-root.Mui-error': {
                      color: AppStyles.colors.error.main,
                    },
                    '& .MuiFormHelperText-root.Mui-error': {
                      color: AppStyles.colors.error.main,
                    },
                    mb: 3,
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ 
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: AppStyles.typography.fontSize.lg,
                    fontWeight: AppStyles.typography.fontWeight.medium,
                    textTransform: 'none',
                    boxShadow: AppStyles.shadows.lg,
                    background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.primary.light} 100%)`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${AppStyles.colors.primary.dark} 0%, ${AppStyles.colors.primary.main} 100%)`,
                      boxShadow: AppStyles.shadows.xl,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                    mb: 2,
                  }}
                  disabled={loading || verificationCode.length !== 6}
                  startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
                >
                  {loading ? t('login.verifying') : t('login.signIn')}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={handleBackToEmail}
                  sx={{
                    textTransform: 'none',
                    color: AppStyles.colors.text.secondary,
                    textDecoration: 'underline',
                  }}
                >
                  {t('login.backToEmail')}
                </Button>
              </>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default LoginPage;
